import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ProgressBar } from "primereact/progressbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Rating } from "primereact/rating";
import dynamicSurveyService, { SurveyAnalysisQuestion } from "../../services/forms/dynamic.service";

const QUESTION_TYPE_LABELS: Record<string, string> = {
  text: "Serbest Metin",
  single_choice: "Tek Seçmeli",
  multiple_choice: "Çok Seçmeli",
  rating: "Derecelendirme",
};

const DynamikAnketAnaliz = () => {
  const { id } = useParams<{ id: string }>();
  const surveyId = Number(id);
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["dynamicSurveyAnalysis", surveyId],
    queryFn: () => dynamicSurveyService.getSurveyAnalysis(surveyId),
    enabled: !!surveyId,
  });

  const analysis = data?.result?.data;

  if (isLoading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="flex flex-column align-items-center justify-content-center p-6">
        <i className="pi pi-exclamation-triangle text-yellow-500" style={{ fontSize: "2rem" }} />
        <p className="text-600 mt-3">Analiz verisi bulunamadı.</p>
        <Button label="Geri Dön" icon="pi pi-arrow-left" outlined onClick={() => navigate("/admin/dinamik-anket-yonetim")} />
      </div>
    );
  }

  const renderQuestionResult = (question: SurveyAnalysisQuestion) => {
    if (question.question_type === "text") {
      const results: { user_email: string; user_osb: string; text_answer: string; submitted_at: string }[] = question.results ?? [];
      return (
        <div className="overflow-x-auto">
          <DataTable value={results} stripedRows emptyMessage="Henüz cevap yok." size="small" tableStyle={{ minWidth: "480px" }}>
            <Column field="user_email" header="Kullanıcı" />
            <Column field="user_osb" header="OSB" />
            <Column field="text_answer" header="Cevap" style={{ whiteSpace: "pre-wrap" }} />
            <Column field="submitted_at" header="Tarih" style={{ width: "130px" }} />
          </DataTable>
        </div>
      );
    }

    if (question.question_type === "single_choice" || question.question_type === "multiple_choice") {
      const results: { id: number; text: string; count: number; percentage: number }[] = question.results ?? [];
      const maxCount = Math.max(...results.map((r) => r.count), 1);
      return (
        <div className="flex flex-column gap-3">
          {results.map((choice) => (
            <div key={choice.id}>
              <div className="flex align-items-center justify-content-between mb-1">
                <span className="text-900 text-sm">{choice.text}</span>
                <span className="text-500 text-sm font-medium">
                  {choice.count} yanıt — %{choice.percentage}
                </span>
              </div>
              <ProgressBar
                value={choice.percentage}
                showValue={false}
                style={{ height: "10px", borderRadius: "5px" }}
                color={choice.count === maxCount ? "var(--primary-color)" : "var(--blue-200)"}
              />
            </div>
          ))}
          {results.length === 0 && <p className="text-500 text-sm m-0">Henüz cevap yok.</p>}
        </div>
      );
    }

    if (question.question_type === "rating") {
      const result: { average: number; distribution: Record<string, number>; count: number } = question.results ?? { average: 0, distribution: {}, count: 0 };
      return (
        <div className="flex flex-column gap-3">
          <div className="flex align-items-center gap-4">
            <div className="flex flex-column align-items-center">
              <span className="text-5xl font-bold text-primary">{result.average}</span>
              <span className="text-500 text-sm">/ 5 ortalama</span>
              <Rating value={Math.round(result.average)} readOnly cancel={false} stars={5} className="mt-1" />
            </div>
            <div className="flex flex-column" style={{ flex: 1 }}>
              {[5, 4, 3, 2, 1].map((star) => {
                const count = result.distribution?.[String(star)] ?? 0;
                const pct = result.count > 0 ? Math.round((count / result.count) * 100) : 0;
                return (
                  <div key={star} className="flex align-items-center gap-2 mb-1">
                    <span className="text-500 text-xs" style={{ width: "30px" }}>{star} ★</span>
                    <ProgressBar value={pct} showValue={false} style={{ flex: 1, height: "8px", borderRadius: "4px" }} />
                    <span className="text-500 text-xs" style={{ width: "40px", textAlign: "right" }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-500 text-sm m-0">Toplam {result.count} değerlendirme</p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="p-3 md:p-4">
      <div className="mb-4">
        <Button
          icon="pi pi-arrow-left"
          label="Yönetim Paneline Dön"
          text
          severity="secondary"
          className="mb-2 p-0"
          onClick={() => navigate("/admin/dinamik-anket-yonetim")}
        />
        <h2 className="text-900 font-bold text-2xl m-0">{analysis.survey_title}</h2>
        {analysis.survey_description && <p className="text-500 mt-1 mb-0">{analysis.survey_description}</p>}
      </div>

      <div className="grid mb-4">
        <div className="col-12 md:col-4">
          <Card className="text-center shadow-1 border-round-xl">
            <span className="text-5xl font-bold text-primary">{analysis.total_responses}</span>
            <p className="text-500 mt-1 mb-0 text-sm">Toplam Katılım</p>
          </Card>
        </div>
        <div className="col-12 md:col-4">
          <Card className="text-center shadow-1 border-round-xl">
            <span className="text-5xl font-bold text-primary">{analysis.questions.length}</span>
            <p className="text-500 mt-1 mb-0 text-sm">Toplam Soru</p>
          </Card>
        </div>
        <div className="col-12 md:col-4">
          <Card className="text-center shadow-1 border-round-xl">
            <span className="text-5xl font-bold text-primary">
              {analysis.questions.length > 0
                ? Math.round((analysis.total_responses / Math.max(analysis.total_responses, 1)) * 100)
                : 0}
              %
            </span>
            <p className="text-500 mt-1 mb-0 text-sm">Tamamlanma Oranı</p>
          </Card>
        </div>
      </div>

      {analysis.total_responses === 0 ? (
        <div className="flex flex-column align-items-center justify-content-center p-6 surface-100 border-round-xl">
          <i className="pi pi-inbox text-400" style={{ fontSize: "2.5rem" }} />
          <p className="text-500 mt-3 mb-0">Bu ankete henüz kimse katılmamış.</p>
        </div>
      ) : (
        <div className="flex flex-column gap-4">
          {analysis.questions.map((question, index) => (
            <Card
              key={question.id}
              className="shadow-1 border-round-xl"
              style={{ borderLeft: "4px solid var(--primary-color)" }}
            >
              <div className="flex align-items-start gap-2 mb-3">
                <span
                  className="flex align-items-center justify-content-center border-circle font-bold text-sm flex-shrink-0 text-white"
                  style={{ width: "28px", height: "28px", background: "var(--primary-color)" }}
                >
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="text-900 font-semibold m-0" style={{ lineHeight: "1.5" }}>{question.text}</p>
                  <Tag
                    value={QUESTION_TYPE_LABELS[question.question_type] ?? question.question_type}
                    severity="info"
                    className="mt-1"
                    style={{ fontSize: "0.7rem" }}
                  />
                </div>
              </div>
              {renderQuestionResult(question)}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DynamikAnketAnaliz;
